import { createTheme } from '@mui/material/styles';

// Design tokens: the code form of /DESIGN.md. Pages take colors and type from here, never raw hex.
export const tokens = {
  ground: '#FFFFFF',
  wash: '#F5F6F8',
  ink: '#1B2230',
  muted: '#5B6472',
  subtle: '#6B7380',
  line: '#E3E6EA',
  control: '#C9CED5',
  signal: '#1F5DA8',
  signalDark: '#174A87',
  selected: '#EEF4FB',
  gain: '#13794A',
  loss: '#B42318',
  font: '"Libre Franklin", Arial, sans-serif',
  container: 1180,
} as const;

const theme = createTheme({
  palette: {
    primary: { main: tokens.signal, dark: tokens.signalDark, contrastText: '#FFFFFF' },
    secondary: { main: tokens.ink },
    success: { main: tokens.gain },
    error: { main: tokens.loss },
    text: { primary: tokens.ink, secondary: tokens.muted },
    divider: tokens.line,
    background: { default: tokens.ground, paper: tokens.ground },
  },
  shape: { borderRadius: 4 },
  typography: {
    fontFamily: tokens.font,
    fontSize: 15,
    h1: { fontSize: '2.125rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h2: { fontSize: '1.3125rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h3: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.3125rem', fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontSize: '1.0625rem', fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, color: tokens.muted },
    body1: { fontSize: '0.9375rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', color: tokens.subtle },
    // Sentence case, not tracked caps: overline renders like a small heading.
    overline: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0, lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: tokens.ground, fontVariantNumeric: 'tabular-nums' },
        a: { color: tokens.signal },
        '*:focus-visible': { outline: `2px solid ${tokens.signal}`, outlineOffset: 2 },
      },
    },
    // Flat surfaces: no elevation anywhere. Cards that remain are a 1px frame, not a lifted tile.
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { border: `1px solid ${tokens.line}`, borderRadius: 6, boxShadow: 'none' } },
    },
    MuiAppBar: { defaultProps: { elevation: 0 } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 4, boxShadow: 'none' } },
    },
    MuiChip: {
      // Kept for existing pages until each is rebuilt; squared off so they stop reading as pills.
      styleOverrides: { root: { borderRadius: 4, fontWeight: 600 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokens.line}` },
        head: { fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, borderBottom: `2px solid ${tokens.ink}` },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 4 } },
    },
    MuiLink: { defaultProps: { underline: 'hover' } },
  },
});

export default theme;
